import { useCallback, useState, useEffect, useRef, useMemo } from 'react'
import zionDebug from '@fs/zion-debug'
import { useFeatureFlag } from '@fs/zion-flags'
import { trackEvent } from '@fs/zion-analytics'
import { encodeArk } from '@fs/zion-ark'
import { useTranslation } from 'react-i18next'
import isEmpty from 'lodash.isempty'
import {
  addSupers,
  addTypeDataToElements,
  convertToSLSRectangle,
  getAllFieldsOfCertainTypes,
  getNestedSubelementsAndFloatingElementsByType,
  processSlsElements,
  requestSLSImageData,
  useSlsTypes,
} from '@fs/zion-sls'
import { useQueryParams } from '@fs/zion-router'
import cloneDeep from 'lodash.clonedeep'
import { errorCollector } from '../../../../shared/utils/utils'
import {
  getTaskList,
  reserveTask,
  checkTask,
} from '../../../../shared/services/recordEnhancement/recordEnhancementService'
import { getImageArkUrl, arkToApid } from '../../../../shared/services/das/dasService'
import {
  getImagePermissions,
  getPersonOrRecordArkFromImage,
  getRecordPermissions,
} from '../../../../shared/services/platform/platformService'
import { FullNameApp, headers } from '../../../../shared/services/config'
import { getPan, getZoom } from '../../../utils/zoomPanUtils'
import { useR2Highlights } from '../../../providers/r2Provider/R2Context'

const debug = zionDebug('rip:useQuickNameTasks') // to turn this on do this in the console: localStorage.debug = 'fs:rip:useQuickNameTasks'

const useQuickNameTasks = ({
  debugArk,
  placeCode,
  recordLanguage,
  action,
  surname,
  finishedDialogOverlay,
  setZoomPan,
  setHighlights,
}) => {
  const [tasks, setTasks] = useState([])

  // Index is the index of the tasks, not the done count.  It is for internal use, not user use.
  const [index, setIndex] = useState(0)
  const [loading, setLoading] = useState()
  const [currentTask, setCurrentTask] = useState({}) // Each name correction task sls data
  const shared_dasProxy = useFeatureFlag('shared_dasProxy')
  const [error, setError] = useState()
  const [imageAuth, setImageAuth] = useState()
  const artifactId = useRef()
  const artifactUrl = useRef()
  const [slsElements, setSlsElements] = useState()
  const [disableBackButton, setDisableBackButton] = useState(false)
  const { displayHighlights } = useR2Highlights()
  const { setQuery } = useQueryParams()

  /* This is the number of tasks that the user has completed. It is used to determine when the finished overlay pops up.
  It has nothing to do with the number of tasks on the image, only the number of tasks the user has completed with respect to how many they chose to do. */
  const [doneCount, setDoneCount] = useState(0)
  /* This is the number of tasks that the user commits to. It has nothing to do with the number of tasks returned from a call to the back end.
  It determines when the finished overlay pops up, at which time the user can choose to continue (reset the done count, and potentially the maxTasks count) */
  const [maxTasks, setMaxTasks] = useState(20)

  const [t] = useTranslation()
  const { data: dataTypes } = useSlsTypes({
    fsUserAgentChain: headers['FS-User-Agent-Chain'],
    dispatchTypes: false,
    lang: 'en',
  })

  const loadingDataRef = useRef(false)

  const setImagePermission = useCallback(async (taskList, fsUserAgentChain) => {
    let imageAuthorize = false
    try {
      let imagePermissions = false
      let slsData
      try {
        slsData = await requestSLSImageData({ partitionKey: taskList[0].partitionKey, fsUserAgentChain })
      } catch (err) {
        if (err?.response?.status === 403) return false
        throw new Error(err)
      }
      addSupers(slsData.elements)
      setSlsElements(slsData)
      const recordPermissions = await getRecordPermissions(slsData.elements, taskList[0]?.id)
      if (recordPermissions) {
        imagePermissions = await getImagePermissions(null, taskList[0].partitionKey)
        imageAuthorize = recordPermissions && imagePermissions
      }
      debug('Image Auth: ', {
        imagePermissions,
        recordPermissions,
        authorized: imageAuthorize,
      })
    } catch (err) {
      if (err?.response?.status) {
        setError(err)
        errorCollector(err, taskList[0]?.partitionKey, {
          codeId: 661,
          status: err?.response?.status,
          responseBody: err?.response?.data,
        })
        setImageAuth(false)
      }
    } finally {
      setImageAuth(imageAuthorize)
    }
    return imageAuthorize
  }, [])

  const getUrl = useCallback(
    (partitionKey) => {
      return artifactUrl.current || getImageArkUrl(partitionKey, shared_dasProxy.isOn) // fallback to unsigned url if necessary
    },
    [shared_dasProxy.isOn]
  )

  const getAttachedRects = useCallback(
    (taskHighlights) => {
      const attachedHighlights = []
      if (taskHighlights) {
        taskHighlights.forEach((h) => {
          attachedHighlights.push(convertToSLSRectangle(h))
        })
      } else if (displayHighlights?.ids) {
        displayHighlights.ids.forEach((id) => {
          if (displayHighlights.highlights[id]?.attached)
            attachedHighlights.push(convertToSLSRectangle(displayHighlights.highlights[id]))
        })
      }
      return attachedHighlights
    },
    [displayHighlights?.highlights, displayHighlights?.ids]
  )

  const handleSettingZoom = useCallback(
    (rects) => {
      const { panX, panY } = getPan(rects)
      const zoom = getZoom(rects, true)
      setZoomPan({ zoom, panX, panY })
    },
    [setZoomPan]
  )

  const setNewCurrentTask = useCallback(
    (taskIndex, currentTasks) => {
      const newCurrentTask = currentTasks ? currentTasks?.[taskIndex] : tasks?.[taskIndex]
      setCurrentTask(newCurrentTask || {})
      if (newCurrentTask) setQuery({ autoGenImageArk: newCurrentTask?.partitionKey })
      return newCurrentTask
    },
    [setQuery, tasks]
  )

  const initializeTask = useCallback(
    ({ taskHighlights }) => {
      const attachedRects = getAttachedRects(taskHighlights)
      // Recenter around highlights when getting a new task
      if (attachedRects) handleSettingZoom(attachedRects)
    },
    [getAttachedRects, handleSettingZoom]
  )

  const setNameCorrectionsData = useCallback(
    (slsImageData) => {
      const newTasks = slsImageData?.slsData?.map((x) => {
        x.artifactUrl = getUrl(x.partitionKey)
        x.apid = artifactId.current
        return x
      })
      setTasks(newTasks)
      setNewCurrentTask(0, newTasks)
      setImagePermission(newTasks, FullNameApp)
    },
    [getUrl, setImagePermission, setNewCurrentTask]
  )

  // This is only needed for debugging specific arks
  // The tasks generated here will only an approximation to what the back end usually does for us
  const specifyImageArkForQuickName = useCallback(async () => {
    // get and process sls data
    const slsImageInfo = await requestSLSImageData({
      partitionKey: debugArk,
      fsUserAgentChain: FullNameApp,
      includeActions: false,
    })
    const ark = slsImageInfo.elements[0].partitionKey
    artifactId.current = await arkToApid(ark)
    const { images } = processSlsElements([
      {
        elements: slsImageInfo.elements,
        danglingSupers: slsImageInfo.danglingSupers,
        danglingElementTypes: slsImageInfo.danglingElementTypes,
        danglingElementDescriptions: slsImageInfo.danglingElementDescriptions,
        partitionKey: encodeArk(artifactId.current),
      },
    ])
    addTypeDataToElements(images, dataTypes, t('type-unknown.label', '<Unknown Type>'))

    // generate the tasks since we usually get this from the backend
    const nameElements = getNestedSubelementsAndFloatingElementsByType(images[0], 'NAME', false).filter(
      (el) => el.elementType === 'NAME'
    )
    const nameFields = nameElements
      .flatMap((nameElement) =>
        getAllFieldsOfCertainTypes(nameElement, ['NAME_SURN', 'NAME_GN', 'NAME_PRE', 'NAME_SUF'])
      )
      .filter((field) => field.rects)

    // set the data like normal
    if (nameFields) {
      artifactUrl.current = getUrl(ark)

      try {
        setNameCorrectionsData({ slsData: nameFields })
        setLoading(false)
      } catch (err) {
        setLoading(false)
        setError(true)
      }
    }
  }, [dataTypes, getUrl, debugArk, setNameCorrectionsData, t])

  const getMoreTasks = useCallback(async () => {
    const isFirstRequest = !tasks || tasks?.length === 0
    const isLastTask = index === tasks?.length
    debug('getMoreTasks start: ', {
      isFirstRequest,
      isLastTask,
      loadingDataRef: loadingDataRef.current,
      debugArk,
      tasksLength: tasks?.length,
    })
    if (!debugArk && !loadingDataRef.current && (isFirstRequest || isLastTask)) {
      loadingDataRef.current = true
      debug('getMoreTasks: ', {
        length: tasks?.length,
        isLastTask,
        index,
        isFirstRequest,
        doneCount,
        maxTasks,
        currentTask,
      })
      const apid = currentTask && currentTask.apid
      debug('apid: ', apid)
      let newTasks
      try {
        setLoading(true)
        newTasks = await getTaskList({
          placeCode,
          language: recordLanguage,
          isFirstRequest,
          apid,
          action,
          taskType: 'Name Correction',
          surname,
        })
        setLoading(false)
        debug('New Tasks: ', { newTasks })
        if (newTasks) {
          artifactId.current = newTasks.artifactId
          artifactUrl.current = newTasks.artifactUrl
          setNameCorrectionsData(newTasks)
          setIndex(0)
          setError(false)
        } else {
          setLoading(false)
          setError(true)
        }
      } catch (err) {
        if (err?.message !== 'NoMoreTasks' && err?.message !== 'TaskExpired' && err?.message !== 'No names on task') {
          errorCollector(err, apid, {
            codeId: 660,
            status: err?.response?.status,
            responseBody: err?.response?.data,
          }) // We don't need to be alerted for this error.
        }
        setLoading(false)
        setError(err)
      }
      loadingDataRef.current = false
    } else if (!loadingDataRef.current && debugArk && !tasks.length) {
      specifyImageArkForQuickName()
    }
  }, [
    tasks,
    index,
    debugArk,
    doneCount,
    maxTasks,
    currentTask,
    placeCode,
    recordLanguage,
    action,
    surname,
    setNameCorrectionsData,
    specifyImageArkForQuickName,
  ])

  /**
   * Edit the tasks for name corrections with the new saved value.
   * @param editedValue
   */
  const updateNameCorrectionTaskValue = (editedValue) => {
    const newTasks = [...tasks]
    const taskIndex = newTasks.findIndex((task) => task.id === currentTask.id)
    tasks[taskIndex].fieldValues[0].origValue.text = editedValue
    setTasks(newTasks)
  }

  useEffect(() => {
    if (!isEmpty(dataTypes)) getMoreTasks()
  }, [dataTypes, getMoreTasks])

  /**
   * Ensures a valid index within the tasks array starting from a given index.
   * This skips any tasks that have been marked as "not a person".
   *
   * Helper for gotoPreviousTask function.
   *
   * @param {Array} tasksToCheck - The array of tasks to check.
   * @param {number} startingIndex - The index to start checking from.
   * @returns {number} - The first valid index where `notAPersonWasClicked` is false, or 0 if startingIndex is less than 0.
   */
  const guaranteeValidPreviousIndex = useCallback((tasksToCheck, startingIndex) => {
    if (startingIndex < 0) return 0

    // if this is the last task, but maxTasks hasn't been reached, then we need to return an index greater than the length of the tasks
    if (startingIndex >= tasksToCheck.length) return startingIndex
    for (let i = startingIndex; i >= 0; i--) {
      if (!tasksToCheck[i].notAPersonWasClicked) return i
    }
    console.error('No valid index found in guaranteeValidPreviousIndex')
    return -1 // should never happen
  }, [])

  /**
   * Ensures a valid index within the tasks array starting from a given index.
   * This skips any tasks that have been marked as "not a person".
   *
   * Helper for gotoNextTask function.
   *
   * @param {Array} tasksToCheck - The array of tasks to check.
   * @param {number} startingIndex - The index to start checking from.
   * @returns {number} - The first valid index where `notAPersonWasClicked` is false, or 0 if startingIndex is less than 0.
   */
  const guaranteeValidNextIndex = useCallback((tasksToCheck, startingIndex) => {
    if (startingIndex < 0) return 0
    // if this is the last task, but maxTasks hasn't been reached, then we need to return an index greater than the length of the tasks
    if (startingIndex >= tasksToCheck.length) return startingIndex
    for (let i = startingIndex; i < tasksToCheck.length; i++) {
      if (!tasksToCheck[i].notAPersonWasClicked) return i
    }
    console.error('No valid index found in guaranteeValidNextIndex')
    return -1 // should never happen
  }, [])

  /**
   * Navigates to the previous task in the list of tasks.
   *
   * This function updates the current task index to the previous task, ensuring the new index is valid.
   * It also checks if all tasks before the new index are marked as "not a person" and disables the back button if true.
   * Additionally, it updates the done count and tracks the event of clicking the back button.
   *
   * @callback goToPreviousTask
   * @param {Function} setNewCurrentTask - Function to set the new current task.
   * @param {Function} guaranteeValidPreviousIndex - Function to ensure the new index is valid.
   * @param {number} index - The current index of the task.
   * @param {Array} tasks - The list of tasks.
   */
  const goToPreviousTask = useCallback(() => {
    const startingIndex = index - 1
    const newIndex = guaranteeValidPreviousIndex(tasks.slice(0, startingIndex + 1), startingIndex) // exclude the current task
    let allRemainingTasksBeforeNewIndexAreNotAPerson = true
    for (let i = 0; i < newIndex; i++) {
      if (!tasks[i].notAPersonWasClicked) {
        allRemainingTasksBeforeNewIndexAreNotAPerson = false
        break
      }
    }
    setDisableBackButton(allRemainingTasksBeforeNewIndexAreNotAPerson)

    setDoneCount(newIndex)
    setIndex(() => {
      setNewCurrentTask(newIndex)
      return newIndex
    })

    trackEvent({
      event_name: 'click_action',
      link_name: `Back`,
    })
  }, [setNewCurrentTask, guaranteeValidPreviousIndex, index, tasks])

  /**
   * Checks if the current task and all previous tasks are marked as "not a person".
   *
   * Helper for goToNextTask.
   *
   * @param {Object} args - The arguments object.
   * @param {boolean} args.justMarkedNotAPerson - Indicates if the current task was just marked as "not a person".
   * @returns {boolean} - Returns true if the current task and all previous tasks are marked as "not a person", otherwise false.
   */
  const isTaskAndPrevTasksNotAPerson = useCallback(
    (args) => {
      if (!args?.justMarkedNotAPerson) return false
      const newTasks = cloneDeep(tasks)
      newTasks[index].notAPersonWasClicked = true
      setTasks(newTasks)

      let allTasksBeforeAreNotAPerson = true
      for (let i = 0; i < index; i++) {
        if (!tasks[i].notAPersonWasClicked) {
          allTasksBeforeAreNotAPerson = false
          break
        }
      }
      return allTasksBeforeAreNotAPerson
    },
    [index, tasks]
  )

  /**
   * Advances to the next task in the sequence.
   * This function handles the transition to the next task. It performs the following actions:
   * - Disables the back button if the current task and all previous tasks are marked as not a person.
   * - Increases the doneCount and index, or opens the finished overlay if all tasks are completed.
   * - Skips over tasks marked as not a person when moving forward.
   * - Initializes the new current task and its highlights.
   *
   * @function
   * @param {Object} args - The arguments for the function.
   * @returns {void}
   */
  const goToNextTask = useCallback(
    (args) => {
      // If the task was just marked as not a person, and all tasks before that were also marked as not a person, then disable the back button
      setDisableBackButton(isTaskAndPrevTasksNotAPerson(args))

      // Either increase the doneCount and index or open the finished overlay
      debug('goToNextTask: ', { index, doneCount, maxTasks })
      if (maxTasks === null || doneCount + 1 < maxTasks) {
        // For when a user has marked a task, as not a person, then gone back, we need to skip over it going forward again

        const newIndex = guaranteeValidNextIndex(tasks, index + 1, true)
        setDoneCount(doneCount + 1)
        setIndex(() => {
          if (tasks && tasks.length && tasks[newIndex]) {
            const newCurrentTask = setNewCurrentTask(newIndex)
            const taskHighlights = setHighlights({ newCurrentTask })
            initializeTask({ newCurrentTask, taskHighlights })
            debug('goToNextTask: ', { maxTasks, doneCount: doneCount + 1, index: index + 1 })
          }
          return newIndex
        })
      } else finishedDialogOverlay.handleClick()
    },
    [
      doneCount,
      finishedDialogOverlay,
      index,
      initializeTask,
      maxTasks,
      setHighlights,
      setNewCurrentTask,
      tasks,
      isTaskAndPrevTasksNotAPerson,
      guaranteeValidNextIndex,
    ]
  )

  const handleContinue = async (max) => {
    function updateTask(newTaskIndex, newTasks) {
      const newCurrentTask = setNewCurrentTask(newTaskIndex, newTasks)
      const taskHighlights = setHighlights({ newCurrentTask, allHighlights: tasks.highlights })
      initializeTask({ newCurrentTask, taskHighlights })
      setDoneCount(0)
      setMaxTasks(max)
      finishedDialogOverlay.handleClick()
    }

    // Set tasks for name corrections
    setTasks((prev) => {
      const newTasks = prev.slice(index + 1)
      setIndex(0)
      updateTask(0, newTasks)
      return newTasks
    })
  }

  // If the person/record ark cannot be found, there is no point showing these buttons, because they won't work
  const showRecordDetailsAndAttachToTree = useMemo(() => {
    try {
      return Boolean(getPersonOrRecordArkFromImage(slsElements?.elements, currentTask.id))
    } catch (err) {
      return false
    }
  }, [currentTask.id, slsElements?.elements])

  const handleClickRecordDetails = async () => {
    trackEvent({
      event_name: 'click_action',
      link_name: `RecordDetails`,
    })
    const personOrRecordArk = getPersonOrRecordArkFromImage(slsElements.elements, currentTask.id)
    window.open(`/ark:/61903/${personOrRecordArk}`, '_blank') ||
      window.location.assign(`/ark:/61903/${personOrRecordArk}`)
  }

  const handleClickAttachToTree = async () => {
    const linkerArk = getPersonOrRecordArkFromImage(slsElements.elements, currentTask.id)
    window.open(`/search/linker?ark=${encodeURIComponent(`/ark:/61903/${linkerArk}`)}`, '_blank') ||
      window.location.assign(`/search/linker?ark=${encodeURIComponent(`/ark:/61903/${linkerArk}`)}`)
  }
  const getNewImage = useCallback(async () => {
    try {
      setLoading(true)
      // Use reserveTask directly instead of getCurrentTask (which getTaskList uses for isFirstRequest)
      const { data, headers: reserveHeaders } = await reserveTask(recordLanguage, placeCode, 'Name Correction', surname)

      const newTasks = await checkTask(data, reserveHeaders, true)

      if (newTasks) {
        artifactId.current = newTasks.artifactId
        artifactUrl.current = newTasks.artifactUrl
        setNameCorrectionsData(newTasks)
        setIndex(0)
        setError(false)
      }
      setLoading(false)
    } catch (err) {
      console.error('Error getting new image:', err)
      setLoading(false)
      setError(err)
    }
  }, [placeCode, recordLanguage, surname, setNameCorrectionsData])

  debug('Return: ', {
    imageAuth,
    tasks,
    currentTask,
    error,
    doneCount,
    index,
    maxTasks,
    dataTypes,
  })

  return {
    goToPreviousTask,
    goToNextTask,
    handleContinue,
    updateNameCorrectionTaskValue,
    showRecordDetailsAndAttachToTree,
    handleClickRecordDetails,
    handleClickAttachToTree,
    doneCount,
    currentTask,
    imageAuth,
    loading,
    error,
    disableBackButton,
    maxTasks,
    index,
    getNewImage,
  }
}

export default useQuickNameTasks
